/**
 * FairwayBid – Mitgliedschaften für Händler und Hersteller
 *
 * Auf der Seite "So funktioniert es" sind drei Pakete versprochen. Dieses Modul
 * ist die eine Stelle, an der die Regeln dazu stehen – Kennzeichnung im Profil,
 * Angebotskontingent und die Gründungsmitglieder-Regel.
 *
 * Woher kommt das Paket eines Händlers?
 * Aus dem Nutzerfeld "Mitgliedschaft" (membership_level), das in der Console
 * unter Users -> User fields als Metadatenfeld gepflegt ist. Metadaten setzt
 * nur der Betreiber – genau richtig für ein bezahltes Paket, das ein Händler
 * nicht selbst umstellen können soll. Werte: tier_basis, tier_professional,
 * tier_premium. Solange nichts gesetzt ist, gilt Basis.
 */

export const BASIS = 'basis';
export const PROFESSIONAL = 'professional';
export const PREMIUM = 'premium';

/**
 * Die Pakete, wie sie auf der Preisseite stehen.
 * offersPerMonth: null bedeutet unbegrenzt.
 */
export const PLANS = {
  [BASIS]: { key: BASIS, label: 'Basis', price: '0 €', offersPerMonth: 2, badge: false },
  [PROFESSIONAL]: {
    key: PROFESSIONAL,
    label: 'Professional',
    price: '49,99 €',
    offersPerMonth: 15,
    badge: true,
  },
  [PREMIUM]: {
    key: PREMIUM,
    label: 'Premium',
    price: '99,99 €',
    offersPerMonth: null,
    badge: true,
  },
};

// Gründungsmitglieder: wer sich bis zu diesem Datum registriert,
// gibt bis zum Stichtag unbegrenzt viele Angebote ab.
export const FOUNDING_SIGNUP_UNTIL = new Date('2026-12-31T23:59:59Z');
export const FOUNDING_BENEFIT_UNTIL = new Date('2027-03-31T23:59:59Z');

/**
 * Paket eines Nutzers.
 *
 * @param {Object} user User- oder CurrentUser-Entity
 * @returns {Object} Eintrag aus PLANS, im Zweifel Basis
 */
// Die Werte, wie sie im Console-Feld stehen.
export const PLAN_BY_TIER = {
  tier_basis: BASIS,
  tier_professional: PROFESSIONAL,
  tier_premium: PREMIUM,
};

export const getPlan = user => {
  const profile = user?.attributes?.profile;
  const tier = profile?.metadata?.membership_level;
  // publicData.plan bleibt als Rueckfall stehen, falls ein Konto noch nach dem
  // alten Schema gepflegt ist.
  const key = PLAN_BY_TIER[tier] || profile?.publicData?.plan;
  return PLANS[key] || PLANS[BASIS];
};

/**
 * Gründungsmitglied? Registrierung vor dem Stichtag und Vorteil noch nicht abgelaufen.
 *
 * @param {Object} user User-Entity
 * @param {Date} now Vergleichszeitpunkt (für Tests überschreibbar)
 * @returns {boolean}
 */
export const isFoundingMember = (user, now = new Date()) => {
  const createdAt = user?.attributes?.createdAt;
  if (!createdAt) {
    return false;
  }
  const created = new Date(createdAt);
  return created <= FOUNDING_SIGNUP_UNTIL && now <= FOUNDING_BENEFIT_UNTIL;
};

/**
 * Wie viele Angebote darf dieser Nutzer diesen Monat abgeben?
 *
 * @param {Object} user User-Entity
 * @param {Date} now Vergleichszeitpunkt
 * @returns {{plan: Object, limit: number|null, unlimited: boolean, founding: boolean}}
 *          limit null = unbegrenzt
 */
export const getOfferAllowance = (user, now = new Date()) => {
  const plan = getPlan(user);
  const founding = isFoundingMember(user, now);
  const limit = founding ? null : plan.offersPerMonth;
  return { plan, limit, unlimited: limit === null, founding };
};

/**
 * Darf noch ein Angebot abgegeben werden?
 *
 * @param {Object} user User-Entity
 * @param {number} offersThisMonth bereits abgegebene Angebote im laufenden Monat
 * @param {Date} now Vergleichszeitpunkt
 * @returns {{allowed: boolean, remaining: number|null, allowance: Object}}
 */
export const canSubmitOffer = (user, offersThisMonth = 0, now = new Date()) => {
  const allowance = getOfferAllowance(user, now);
  if (allowance.unlimited) {
    return { allowed: true, remaining: null, allowance };
  }
  const remaining = Math.max(0, allowance.limit - offersThisMonth);
  return { allowed: remaining > 0, remaining, allowance };
};

/**
 * Zeitvorsprung: Wie lange muss ein Haendler warten, bis eine neu
 * veroeffentlichte Anfrage fuer ihn freigegeben ist?
 *
 * Premium sieht sofort, Professional zwoelf Stunden spaeter, Basis einen Tag
 * spaeter. Damit verkauft der Tarif Geschwindigkeit, nicht Zugang: Jede Anfrage
 * erreicht am Ende jeden passenden Haendler, die Golfanlage bekommt also
 * weiterhin die volle Zahl an Angeboten. Genau das steht auch in den AGB –
 * dort sind die Pakete ueber die Zahl der Angebote definiert, nicht darueber,
 * was jemand sehen darf.
 */
export const VISIBILITY_DELAY_HOURS = {
  [PREMIUM]: 0,
  [PROFESSIONAL]: 12,
  [BASIS]: 24,
};

const MS_PER_HOUR = 60 * 60 * 1000;

export const getVisibilityDelayHours = (user, now = new Date()) => {
  // Gruendungsmitglieder bekommen den Premium-Vorsprung als Teil ihres Vorteils.
  if (isFoundingMember(user, now)) return 0;
  const plan = getPlan(user);
  return VISIBILITY_DELAY_HOURS[plan.key] ?? VISIBILITY_DELAY_HOURS[BASIS];
};

/**
 * Ab wann ist diese Anfrage fuer diesen Haendler freigegeben?
 * @returns {Date|null} null, wenn die Anfrage kein Veroeffentlichungsdatum hat
 */
export const getReleaseTime = (listing, user, now = new Date()) => {
  const published = listing?.attributes?.publishedAt || listing?.attributes?.createdAt;
  if (!published) return null;
  const start = published instanceof Date ? published : new Date(published);
  if (Number.isNaN(start.getTime())) return null;
  return new Date(start.getTime() + getVisibilityDelayHours(user, now) * MS_PER_HOUR);
};

export const isReleasedForDealer = (listing, user, now = new Date()) => {
  const release = getReleaseTime(listing, user, now);
  return release ? release <= now : true;
};

/**
 * Wartezeit in Worten, fuer den Hinweis an gesperrten Anfragen.
 */
export const formatWaitingTime = (release, now = new Date()) => {
  if (!release) return null;
  const minutes = Math.ceil((release.getTime() - now.getTime()) / (60 * 1000));
  if (minutes <= 0) return null;
  if (minutes < 60) return `in ${minutes} Minuten`;
  const hours = Math.ceil(minutes / 60);
  return hours === 1 ? 'in einer Stunde' : `in ${hours} Stunden`;
};
